import { useState } from 'react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import type { CohortLookback, CohortLookbackUnit } from '../services/measure.service';

const UNSET_VALUE = '__default__';

const UNIT_OPTIONS: { value: CohortLookbackUnit; label: string }[] = [
  { value: 'DAY', label: 'Days' },
  { value: 'WEEK', label: 'Weeks' },
  { value: 'MONTH', label: 'Months' },
  { value: 'QUARTER', label: 'Quarters' },
  { value: 'YEAR', label: 'Years' },
];

interface CohortLookbackInputProps {
  id?: string;
  value: CohortLookback | null | undefined;
  onChange: (value: CohortLookback | null) => void;
  /** Label of the option that clears the lookback, e.g. "Metric default". */
  unsetLabel: string;
}

/** Count + unit picker for a retained/churned cohort lookback window; the unset option means "use the default". */
export function CohortLookbackInput({ id, value, onChange, unsetLabel }: CohortLookbackInputProps) {
  // The count is edited as text so it can be cleared and retyped; only a whole
  // number >= 1 is committed, and blurring an invalid draft restores the last one.
  const [countDraft, setCountDraft] = useState(value ? String(value.count) : '');
  const [draftFor, setDraftFor] = useState(value);
  if (value !== draftFor) {
    setDraftFor(value);
    setCountDraft(value ? String(value.count) : '');
  }

  return (
    <div className="flex items-center gap-2">
      {value && (
        <Input
          type="number"
          min={1}
          step={1}
          className="w-20"
          value={countDraft}
          onChange={(e) => {
            setCountDraft(e.target.value);
            const count = Number(e.target.value);
            if (Number.isInteger(count) && count >= 1) onChange({ ...value, count });
          }}
          onBlur={() => setCountDraft(String(value.count))}
          aria-label="Lookback count"
        />
      )}
      <Select
        value={value?.unit ?? UNSET_VALUE}
        onValueChange={(unit) => {
          if (unit === UNSET_VALUE) {
            onChange(null);
          } else {
            onChange({ count: value?.count ?? 1, unit: unit as CohortLookbackUnit });
          }
        }}
      >
        <SelectTrigger id={id} className="flex-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={UNSET_VALUE}>{unsetLabel}</SelectItem>
          {UNIT_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
